import { useState } from "react";
import { toast } from "sonner";
import { X, Loader2, Send, ShieldCheck, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { trackLead } from "@/lib/analytics";

/**
 * Quote-request dialog for the platform-specific review-removal services
 * (Reddit, Glassdoor, Airbnb, Trustpilot).
 * No payment happens here — we capture the lead + notify admin + close.
 */
export default function QuoteRequestDialog({ open, platform, onClose }) {
  const [form, setForm] = useState({
    name: "",
    business_name: "",
    email: "",
    phone: "",
    profile: "",
    problem_description: "",
  });
  const [loading, setLoading] = useState(false);

  if (!open || !platform) return null;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e?.preventDefault?.();
    const errors = [];
    if (!form.name.trim()) errors.push("Full name");
    if (!form.business_name.trim()) errors.push("Business name");
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      errors.push("A valid email");
    if (!form.problem_description.trim() || form.problem_description.trim().length < 10)
      errors.push("A short description (10+ chars)");
    if (errors.length) {
      toast.error(`Please fill in: ${errors.join(", ")}`);
      return;
    }
    setLoading(true);
    try {
      trackLead("quote_request", { platform });
      await api.post("/quote-requests", {
        platform,
        name: form.name.trim(),
        business_name: form.business_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || undefined,
        profile: form.profile.trim() || undefined,
        problem_description: form.problem_description.trim(),
      });
      toast.success(
        `Quote request received — a ${platform} removal specialist will reply within one business day.`,
        { duration: 7000 }
      );
      setForm({ name: "", business_name: "", email: "", phone: "", profile: "", problem_description: "" });
      onClose();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Couldn't submit your request — please try again in a moment.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] grid place-items-center bg-black/75 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && !loading && onClose()}
      data-testid="quote-dialog"
    >
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0d1424] p-6 sm:p-8 relative my-8">
        {!loading && (
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors"
            aria-label="Close"
            data-testid="quote-dialog-close"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] font-bold">
          <MessageSquare className="w-3.5 h-3.5" />
          {platform} review removal · Quote
        </div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold mt-2 leading-tight">
          Request a quote for {platform} removal
        </h2>
        <p className="mt-2 text-white/55 text-sm leading-relaxed">
          Tell us about the {platform} review(s) that are hurting your business.
          A removal specialist will reply within one business day with an
          ethical, policy-mapped plan and a fixed per-review quote.{" "}
          <strong className="text-white/80">You only pay after removal.</strong>
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Your full name" required>
              <input
                type="text"
                value={form.name}
                onChange={set("name")}
                autoComplete="name"
                placeholder="Jane Rousseau"
                data-testid="quote-name"
                disabled={loading}
                className={inputCls}
              />
            </Field>
            <Field label="Business name" required>
              <input
                type="text"
                value={form.business_name}
                onChange={set("business_name")}
                autoComplete="organization"
                placeholder="Riverside Bistro"
                data-testid="quote-business"
                disabled={loading}
                className={inputCls}
              />
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Email" required>
              <input
                type="email"
                value={form.email}
                onChange={set("email")}
                autoComplete="email"
                placeholder="you@business.com"
                data-testid="quote-email"
                disabled={loading}
                className={inputCls}
              />
            </Field>
            <Field label="Phone / WhatsApp (optional)">
              <input
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                autoComplete="tel"
                placeholder="+92 333 000 0000"
                data-testid="quote-phone"
                disabled={loading}
                className={inputCls}
              />
            </Field>
          </div>

          <Field label={`Your ${platform} profile / listing URL (optional)`}>
            <input
              type="url"
              value={form.profile}
              onChange={set("profile")}
              placeholder="https://…"
              data-testid="quote-profile"
              disabled={loading}
              className={inputCls}
            />
          </Field>

          <Field label="How many reviews and what's the problem?" required>
            <textarea
              rows={4}
              value={form.problem_description}
              onChange={set("problem_description")}
              placeholder={`e.g. Three ${platform} reviews from an ex-employee are hurting our reputation. All three are policy violations because…`}
              data-testid="quote-description"
              disabled={loading}
              className={`${inputCls} h-auto py-2.5 resize-none`}
            />
          </Field>

          <div className="pt-3 flex flex-col-reverse sm:flex-row sm:justify-end sm:items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="text-white/55 hover:text-white text-sm font-semibold h-11 px-4 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              data-testid="quote-submit"
              className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full bg-[#D4AF37] hover:bg-[#c19b2e] text-[#0A0F1C] font-semibold text-sm shadow-lg shadow-[#D4AF37]/25 transition-colors disabled:opacity-70"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send quote request
                </>
              )}
            </button>
          </div>

          <p className="text-[11px] text-white/40 leading-relaxed pt-1 flex items-start gap-1.5">
            <ShieldCheck className="w-3 h-3 mt-0.5 text-[#10B981] shrink-0" />
            <span>
              Ethical only. We only pursue reviews that violate the target
              platform's public content policies. Payment after successful
              removal — never upfront.
            </span>
          </p>
        </form>
      </div>
    </div>
  );
}

const inputCls =
  "w-full h-11 px-3.5 rounded-lg border border-white/10 bg-white/[0.03] text-white text-sm placeholder:text-white/30 focus:border-[#D4AF37]/60 outline-none transition-colors";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-[0.14em] text-white/50 font-semibold mb-1.5">
        {label} {required && <span className="text-[#D4AF37]">*</span>}
      </div>
      {children}
    </label>
  );
}
